import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { Typography } from '@/components/Typography/Typography';
import { DATE_TIME_FORMAT, formatDate } from '@/helpers/dateHelpers/dateHelpers';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { RecipientRequestType } from '@/types/vendorVaultTypes';
import { RecipientRequestActionType } from '@/api/v1/organization/vendor-vault/recipientRequestActionApi';
import { InlineAction } from '@/bundle/_OrgAdmin/pages/MyVaultPage/styles';
import { runRequestAction, VENDOR_VAULT_QUERY_KEYS } from '../api/api';
import { STAMP_LABEL } from '../const/const';
import { RecipientRequestStatus } from './RecipientRequestStatus';
import { TableNew } from '@/components-new/TableNew/TableNew';
import { TableHeadNew } from '@/components-new/TableNew/TableHeadNew';
import { TableHeadCellNew } from '@/components-new/TableNew/TableHeadCellNew';
import { TableBodyNew, TableCellNew, TableRowNew } from '@/components-new/TableNew/styles';
import { Box } from '@/components/Box/Box';
import { ReactNode } from 'react';

const formatSentTo = (sentTo: string) => {
  // Check if the string is purely digits (a phone number)
  if (/^\d+$/.test(sentTo)) {
    // Handle US numbers (10 or 11 digits)
    let digits = sentTo;

    // Strip leading "1" if it's an 11-digit number (US country code)
    if (digits.length === 11 && digits.startsWith('1')) {
      digits = digits.slice(1);
    }

    // Only format if it's a valid 10-digit US number
    if (digits.length === 10) {
      return `+1 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    }
  }

  return sentTo;
};

type RecipientRequestsListType = {
  list: RecipientRequestType[];
  isLoading: boolean;
  typeLabel: string;
  onViewProfile: (request: RecipientRequestType) => void;
  onOpenVerification: (request: RecipientRequestType) => void;
  header: ReactNode;
};

export const RecipientRequestsList = ({
  list = [],
  isLoading,
  typeLabel,
  onViewProfile,
  onOpenVerification,
  header,
}: RecipientRequestsListType) => {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationKey: ['recipient_request_action'],
    mutationFn: ({ id, action }: { id: string; action: RecipientRequestActionType }) => runRequestAction(id, action),
    onSuccess(response, { action }) {
      if (response?.error) return showSnackbarResponseError(response.error);

      if (action === 'revoke') {
        amplitudeService.logEvent(AMPLITUDE_EVENTS.VendorVaultRevokeSuccess);
      }

      queryClient.invalidateQueries({ queryKey: [VENDOR_VAULT_QUERY_KEYS.requests] });
      queryClient.invalidateQueries({ queryKey: VENDOR_VAULT_QUERY_KEYS.counts });
    },
  });

  const stampText = (request: RecipientRequestType) =>
    request.stamp ? `${STAMP_LABEL[request.stamp.kind]} ${formatDate(request.stamp.at, DATE_TIME_FORMAT)}` : '';

  return (
    <TableNew header={header} noItems={`No ${typeLabel} requests yet.`} isLoading={isLoading} itemsCount={list.length}>
      <>
        <TableHeadNew>
          <TableHeadCellNew>RECIPIENT</TableHeadCellNew>
          <TableHeadCellNew>SENT TO</TableHeadCellNew>
          <TableHeadCellNew>TEMPLATE</TableHeadCellNew>
          <TableHeadCellNew>MODE</TableHeadCellNew>
          <TableHeadCellNew hasSort name='date_created'>
            SENT · EXPIRES
          </TableHeadCellNew>
          <TableHeadCellNew>STATUS</TableHeadCellNew>
          <TableHeadCellNew></TableHeadCellNew>
        </TableHeadNew>
        <TableBodyNew>
          {list.map((request) => (
            <TableRowNew key={request.id}>
              <TableCellNew>
                <Typography fontWeight='medium'>{request.recipient_name}</Typography>
                <Typography variant='caption'>{request.audience_label}</Typography>
              </TableCellNew>
              <TableCellNew>{formatSentTo(request.sent_to)}</TableCellNew>
              <TableCellNew>{request.template_label}</TableCellNew>
              <TableCellNew>{request.mode_label}</TableCellNew>
              <TableCellNew>
                {formatDate(request.date_created, DATE_TIME_FORMAT)} · {request.expiry_label}
              </TableCellNew>
              <TableCellNew>
                <RecipientRequestStatus request={request} />
              </TableCellNew>
              <TableCellNew>
                <Box display='flex' gap={'14px'} flexDirection='column' alignItems='flex-start'>
                  {request.has_profile && request.status !== 'closed' && (
                    <ButtonNew size='medium' variant='secondary' onClick={() => onViewProfile(request)}>
                      View profile
                    </ButtonNew>
                  )}

                  {request.is_live && (
                    <InlineAction
                      $danger
                      disabled={isPending}
                      onClick={() => mutate({ id: request.id, action: 'revoke' })}
                    >
                      Revoke
                    </InlineAction>
                  )}

                  {request.can_resend && (
                    <>
                      <InlineAction disabled={isPending} onClick={() => mutate({ id: request.id, action: 'resend' })}>
                        Resend
                      </InlineAction>
                      <InlineAction disabled={isPending} onClick={() => mutate({ id: request.id, action: 'archive' })}>
                        Archive
                      </InlineAction>
                    </>
                  )}

                  {request.has_verification && request.stamp?.kind === 'verified' ? (
                    <InlineAction onClick={() => onOpenVerification(request)}>{stampText(request)}</InlineAction>
                  ) : (
                    request.stamp && <Typography variant='bodySmall2'>{stampText(request)}</Typography>
                  )}
                </Box>
              </TableCellNew>
            </TableRowNew>
          ))}
        </TableBodyNew>
      </>
    </TableNew>
  );
};
